import useModalManager from '@hooks/useModalManager';
import { useClubSlug } from 'components/Clubs/hooks';
import { Blockquote } from '@ui';
import { FormAssistent } from 'components/Formular/FormAssistent';
import Loader from 'components/Loader';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { FormularCard } from './FormularCard';

interface Props {
    name: string;
}

export const FormularPicker = ({ name }: Props) => {
    const clubSlug = useClubSlug();
    const { t } = useTranslation('form');
    const { addModal, removeModal } = useModalManager();
    const { control } = useFormContext();
    const {
        field: { value, onChange },
        formState: { errors }
    } = useController({ control, name });
    const { data, error, mutate } = useSWR(clubSlug ? `club/${clubSlug}/forms` : null);
    const isLoading = !data && !error;
    const formError = errors?.[name];

    const clubForms: any[] = data?.data ?? [];
    const assignedInList = value && clubForms.some((f) => f.id === value);
    // Fetch the currently-assigned form by ID when it isn't in this club's list
    // (e.g. legacy user-owned forms from before the migration). This way it still
    // renders as selected instead of looking like nothing is assigned.
    const { data: extraFormData } = useSWR<{ data: any }>(
        value && !isLoading && !assignedInList ? `form/${value}` : null
    );
    const extraForm = !assignedInList && extraFormData?.data ? extraFormData.data : null;

    const createForm = () =>
        addModal({
            title: t('builder.createTitle'),
            content: (id) => (
                <FormAssistent
                    clubSlug={clubSlug}
                    cb={() => {
                        removeModal(id);
                        void mutate();
                    }}
                />
            ),
            size: 100
        });

    return (
        <>
            <Loader isLoading={isLoading}>
                <div className="tw-grid tw-grid-cols-3 tw-gap-4">
                    {formError && (
                        <div className="tw-grid">
                            <Blockquote color="error" className="tw-w-full tw-px-4 tw-py-6 tw-text-error">
                                <h3>{formError?.message as string}</h3>
                                <p>{t('builder.selectHint')}</p>
                            </Blockquote>
                        </div>
                    )}
                    <div className="tw-grid">
                        <FormularCard clubSlug={clubSlug} callback={() => mutate()} onPress={createForm} />
                    </div>
                    {extraForm && (
                        <div className="tw-grid" key={`extra-${extraForm.id}`}>
                            <FormularCard
                                clubSlug={clubSlug}
                                selected={value === extraForm.id}
                                form={extraForm}
                                callback={(data) => {
                                    if (!data) onChange(null);
                                    mutate();
                                }}
                                onPress={() => onChange(value === extraForm.id ? null : extraForm.id)}
                            />
                        </div>
                    )}
                    {clubForms.map((form) => (
                        <div className="tw-grid" key={form.id}>
                            <FormularCard
                                clubSlug={clubSlug}
                                selected={value === form.id}
                                form={form}
                                callback={(data) => {
                                    if (!data) onChange(null);
                                    mutate();
                                }}
                                onPress={() => onChange(value === form.id ? null : form.id)}
                            />
                        </div>
                    ))}
                </div>
            </Loader>
        </>
    );
};
